import { useEffect, useState, Fragment, useCallback, useRef } from "react";
import {
  Receipt, FileText, Download, Loader2, Save, Eye, Trash2, RefreshCw,
  FileSpreadsheet, Printer, ArrowLeft, Upload, Inbox, Scale, ArrowRight, BarChart3, Mail, Droplets, X,
} from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { downloadInvoicePdf, invoicePdfBlob, invoiceToCsv, downloadCsv } from "../lib/exporters";

const SERVICE_LABELS = {
  HANDLING: "Handling",
  INSPECTION: "Inspection Fee",
  WASHING: "Washing",
  SPECIAL_CLEANING: "Special Cleaning",
  SHORT_PTI: "Short PTI (Visual Inspection)",
  EXTENDED_PTI: "Extended PTI",
  FOOD_UPGRADE: "Food Upgrade",
  STORAGE: "Storage",
};

const fmtMoney = (n, ccy = "USD") => {
  const v = Number(n || 0);
  return `${ccy} ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDateInput = (d) => d.toISOString().slice(0, 10);

// --- Water Solution invoice detail (flat-line shape, no containers[]) ---
function WaterSolutionInvoiceDetail({ invoice, onBack, canDelete, onDelete, onEmail, emailing, setEmailing }) {
  const ccy = invoice.currency;
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const subtotal = Number(invoice.subtotal ?? invoice.totals?.subtotal ?? 0);
  const grand = Number(invoice.total ?? invoice.totals?.grand_total ?? subtotal);

  return (
    <div className="space-y-4" data-testid="invoice-detail-water-solution">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onBack} data-testid="invoice-back">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-primary" onClick={() => downloadInvoicePdf(invoice)} data-testid="invoice-export-pdf">
            <Printer className="w-4 h-4" /> Download PDF
          </button>
          {invoice.id && (
            <button className="ps-btn ps-btn-secondary" onClick={onEmail} data-testid="invoice-email">
              <Mail className="w-4 h-4" /> Email
            </button>
          )}
          {canDelete && invoice.id && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={onDelete} data-testid="invoice-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <EmailInvoiceDialog open={emailing} invoice={invoice} onClose={() => setEmailing(false)} />

      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
              Invoice <span className="ml-1 inline-block px-1.5 py-0.5 bg-[#38BDF8] text-white rounded-sm text-[9px]">WATER SOLUTION</span>
            </div>
            <div className="font-mono font-bold text-lg">{invoice.invoice_no}</div>
            {invoice.status && (
              <span className="ps-pill ps-pill-success mt-1 inline-flex">
                <span className="dot" /> {invoice.status}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand Total</div>
            <div className="font-mono font-black text-3xl text-[#006E9E]">
              {fmtMoney(grand, ccy)}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200">
          {[
            { l: "Carrier", v: invoice.carrier_code || invoice.customer_code || "—" },
            { l: "Period", v: invoice.period || "—" },
            { l: "Jobs", v: lines.length.toLocaleString() },
            { l: "Flat fee", v: fmtMoney(lines[0]?.rate ?? 0, ccy) },
          ].map((m) => (
            <div key={m.l} className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{m.l}</div>
              <div className="font-mono text-base font-bold mt-1">{m.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="invoice-ws-lines-table">
          <thead>
            <tr>
              <th>Service date</th>
              <th>Tank</th>
              <th>PO</th>
              <th>Description</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Rate</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">No line items</td></tr>
            )}
            {lines.map((l, i) => (
              <tr key={i} data-testid={`invoice-ws-line-${i}`}>
                <td className="font-mono text-xs">{(l.service_date || "").slice(0, 10)}</td>
                <td className="font-mono font-semibold">{l.tank_number || "—"}</td>
                <td className="font-mono text-xs">{l.po_number || "—"}</td>
                <td className="text-xs">{l.description || "Water Solution"}</td>
                <td className="text-right font-mono">{l.qty ?? 1}</td>
                <td className="text-right font-mono">{fmtMoney(l.rate, ccy)}</td>
                <td className="text-right font-mono font-bold">{fmtMoney(l.amount, ccy)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td colSpan={6} className="text-right font-bold uppercase tracking-[0.15em] text-[10px] text-gray-600 py-3">Grand total</td>
              <td className="text-right font-mono font-black text-[#006E9E]">{fmtMoney(grand, ccy)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// --- Detail view (used by both saved and freshly generated invoices) ---
function InvoiceDetail({ invoice, onBack, canDelete, onDeleted }) {
  const [expanded, setExpanded] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const ccy = invoice.currency;
  const isWs = (invoice.invoice_type || "").toUpperCase() === "WATER_SOLUTION";

  const handleDelete = async () => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${invoice.id}`);
      toast.success(`Invoice ${invoice.invoice_no} deleted`);
      onDeleted?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Water Solution invoices have a flat line shape (not the containers[]
  // tree). Render a dedicated panel so the summary / line table / totals
  // all match the Water Solution PDF the operator just downloaded.
  if (isWs) {
    return (
      <WaterSolutionInvoiceDetail
        invoice={invoice}
        onBack={onBack}
        canDelete={canDelete}
        onDelete={handleDelete}
        onEmail={() => setEmailing(true)}
        emailing={emailing}
        setEmailing={setEmailing}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="invoice-detail">
      <div className="flex items-center justify-between">
        <button className="ps-btn ps-btn-secondary" onClick={onBack} data-testid="invoice-back">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={() => invoiceToCsv(invoice)} data-testid="invoice-export-csv">
            <FileSpreadsheet className="w-4 h-4" /> CSV
          </button>
          <button className="ps-btn ps-btn-primary" onClick={() => downloadInvoicePdf(invoice)} data-testid="invoice-export-pdf">
            <Printer className="w-4 h-4" /> Download PDF
          </button>
          {invoice.id && (
            <button className="ps-btn ps-btn-secondary" onClick={() => setEmailing(true)} data-testid="invoice-email">
              <Mail className="w-4 h-4" /> Email
            </button>
          )}
          {canDelete && invoice.id && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={handleDelete} data-testid="invoice-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <EmailInvoiceDialog open={emailing} invoice={invoice} onClose={() => setEmailing(false)} />

      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Invoice</div>
            <div className="font-mono font-bold text-lg">{invoice.invoice_no}</div>
            {invoice.status && (
              <span className="ps-pill ps-pill-success mt-1 inline-flex">
                <span className="dot" /> {invoice.status}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand Total</div>
            <div className="font-mono font-black text-3xl text-[#006E9E]">
              {fmtMoney(invoice.totals.grand_total, ccy)}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-gray-200">
          {[
            { l: "Carrier", v: invoice.carrier_code },
            { l: "Containers", v: invoice.totals.containers.toLocaleString() },
            { l: "Line items", v: invoice.totals.line_items.toLocaleString() },
            { l: "Free days", v: invoice.free_days },
            { l: "Period", v: `${invoice.date_from.slice(0,10)} → ${invoice.date_to.slice(0,10)}` },
          ].map((m) => (
            <div key={m.l} className="p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{m.l}</div>
              <div className="font-mono text-base font-bold mt-1">{m.v}</div>
            </div>
          ))}
        </div>
      </div>

      {Array.isArray(invoice.stc_po_numbers) && invoice.stc_po_numbers.length > 0 && (
        <div className="border border-[#006E9E]/30 bg-[#E6F1F7] p-4" data-testid="invoice-stc-po-block">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#006E9E] mb-2">
            STC PO References ({invoice.stc_po_numbers.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {invoice.stc_po_numbers.map((po) => (
              <span key={po} className="font-mono text-xs font-bold px-2 py-1 bg-white border border-[#006E9E]/40 text-[#006E9E]" data-testid={`invoice-stc-po-${po}`}>
                {po}
              </span>
            ))}
          </div>
        </div>
      )}

      {invoice.category_breakdown && invoice.category_breakdown.length > 1 && (
        <div className="border border-gray-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Category breakdown</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="invoice-category-breakdown">
            {invoice.category_breakdown.map((c) => {
              const accent = c.category === "FOOD" ? "#99D420"
                : c.category === "CHEM" ? "#F1471D" : "#5A4E42";
              return (
                <div key={c.category} className="border-l-4 pl-3 py-1.5" style={{ borderColor: accent }} data-testid={`invoice-cat-${c.category}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">{c.category}</div>
                  <div className="font-mono text-lg font-bold mt-0.5">{fmtMoney(c.subtotal, ccy)}</div>
                  <div className="text-[10px] text-gray-500 font-mono">{c.containers} container{c.containers === 1 ? "" : "s"}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="invoice-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Container</th>
              <th>ISO</th>
              <th>Size</th>
              <th>Category</th>
              <th>Customer</th>
              <th className="text-right">Line items</th>
              <th className="text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {invoice.containers.slice(0, 500).map((c, idx) => {
              const isOpen = expanded === c.container_number;
              const cat = c.tank_category || null;
              // Insert a category separator row when we cross a boundary.
              const prevCat = idx > 0 ? (invoice.containers[idx - 1].tank_category || null) : null;
              const showSep = idx === 0 || cat !== prevCat;
              const catLabel = cat || "UNCATEGORIZED";
              const catColor = cat === "FOOD" ? "#99D420" : cat === "CHEM" ? "#F1471D" : "#9CA3AF";
              return (
                <Fragment key={c.container_number}>
                  {showSep && (
                    <tr className="bg-gray-50" data-testid={`invoice-cat-sep-${catLabel}`}>
                      <td colSpan={8} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: catColor }}>
                        ── {catLabel === "FOOD" ? "Food-grade cleanings" : catLabel === "CHEM" ? "Chemical cleanings" : "Uncategorized"}
                      </td>
                    </tr>
                  )}
                  <tr data-testid={`invoice-row-${c.container_number}`}>
                    <td>
                      <button
                        onClick={() => setExpanded(isOpen ? null : c.container_number)}
                        className="text-gray-400 hover:text-gray-700 text-xs font-mono"
                      >{isOpen ? "−" : "+"}</button>
                    </td>
                    <td className="font-mono font-semibold">{c.container_number}</td>
                    <td className="font-mono">{c.iso_type}</td>
                    <td className="font-mono">{c.size_ft}ft</td>
                    <td>
                      {cat ? (
                        <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${catColor}22`, color: catColor }}>
                          {cat}
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="text-xs">{c.customer || "—"}</td>
                    <td className="text-right font-mono">{c.lines.length}</td>
                    <td className="text-right font-mono font-bold">{fmtMoney(c.subtotal, ccy)}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={8} className="bg-gray-50 p-0">
                        <table className="ps-table">
                          <thead>
                            <tr>
                              <th>Service</th>
                              <th>Description</th>
                              <th>Event time</th>
                              <th className="text-right">Qty</th>
                              <th className="text-right">Rate</th>
                              <th className="text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {c.lines.map((l, i) => (
                              <tr key={i}>
                                <td className="font-mono text-xs">{SERVICE_LABELS[l.service] || l.service}</td>
                                <td className="text-xs">{l.description}</td>
                                <td className="font-mono text-xs">{new Date(l.event_time).toLocaleString()}</td>
                                <td className="text-right font-mono">{l.qty}</td>
                                <td className="text-right font-mono">{fmtMoney(l.rate, ccy)}</td>
                                <td className="text-right font-mono font-bold">{fmtMoney(l.amount, ccy)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {invoice.containers.length > 500 && (
              <tr>
                <td colSpan={8} className="text-center text-xs text-gray-400 py-3">
                  Showing first 500 containers of {invoice.containers.length}. Export for full data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Email invoice dialog ---
function EmailInvoiceDialog({ open, invoice, onClose }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [saveDefaults, setSaveDefaults] = useState(false);
  // Snapshot of what the partner's defaults were when the dialog opened —
  // used to (a) show the "Save for next time" checkbox only when the operator
  // actually changed something, and (b) pass to the PATCH that syncs them.
  const [partnerDefaults, setPartnerDefaults] = useState({ to: "", cc: "" });

  useEffect(() => {
    if (!open || !invoice) return;
    const grand = (invoice.totals || {}).grand_total ?? 0;
    setTo("");
    setCc("");
    setSaveDefaults(false);
    setPartnerDefaults({ to: "", cc: "" });
    setSubject(`Portside Invoice ${invoice.invoice_no} — ${invoice.carrier_code} — ${invoice.currency} ${Number(grand).toFixed(2)}`);
    setBody(
      `Dear partner,\n\n` +
      `Please find attached invoice ${invoice.invoice_no} for ${invoice.carrier_code}.\n` +
      `Amount: ${invoice.currency} ${Number(grand).toFixed(2)}\n\n` +
      `Kind regards,\nPortside Operations`
    );
    // Auto-fill both recipient and CC from partner master data.
    if (invoice.carrier_code) {
      api.get(`/partners/${encodeURIComponent(invoice.carrier_code)}`)
        .then(({ data }) => {
          const t = data?.billing_email || "";
          const c = data?.billing_cc || "";
          if (t) setTo(t);
          if (c) setCc(c);
          setPartnerDefaults({ to: t, cc: c });
        })
        .catch(() => { /* partner may not exist yet — leave fields empty */ });
    }
  }, [open, invoice]);

  if (!open || !invoice) return null;

  // Show the "Save for next time" checkbox only when the operator's entered
  // addresses differ from what's stored on the partner — otherwise it's a
  // no-op and just clutters the UI.
  const trimTo = to.trim();
  const trimCc = cc.trim();
  const canSaveDefaults = trimTo && (trimTo !== partnerDefaults.to || trimCc !== partnerDefaults.cc);

  const send = async () => {
    if (!trimTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimTo)) {
      toast.error("Please provide a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const blob = await invoicePdfBlob(invoice);
      const fd = new FormData();
      fd.append("to", trimTo);
      if (trimCc) fd.append("cc", trimCc);
      fd.append("subject", subject);
      fd.append("body", body);
      fd.append("file", blob, `${invoice.invoice_no}.pdf`);
      const { data } = await api.post(`/invoices/${invoice.id}/email`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.skipped) {
        toast.warning("SMTP not configured — email not sent (logged only)");
      } else {
        toast.success(`Invoice emailed to ${trimTo}`);
      }
      // Save the operator's addresses back to the partner so the next
      // FDA/invoice for this customer pre-fills automatically.
      if (saveDefaults && invoice.carrier_code && canSaveDefaults) {
        try {
          await api.patch(`/partners/${encodeURIComponent(invoice.carrier_code)}`, {
            billing_email: trimTo,
            billing_cc: trimCc || null,
          });
          toast.info(`Saved for future ${invoice.carrier_code} FDAs`);
        } catch (e) {
          toast.warning("Email sent, but couldn't save as default — check partner permissions.");
        }
      }
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="email-invoice-dialog"
    >
      <div className="bg-white border border-gray-200 max-w-xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Email invoice</div>
            <div className="font-mono font-bold text-sm mt-0.5">{invoice.invoice_no}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><Trash2 className="w-4 h-4 hidden" /><span className="text-xl leading-none">×</span></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Recipient *</label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="ops@carrier.com"
              className="ps-input font-mono"
              data-testid="email-to"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">CC (comma-separated)</label>
            <input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="optional"
              className="ps-input font-mono"
              data-testid="email-cc"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="ps-input"
              data-testid="email-subject"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              className="ps-input font-mono text-xs"
              data-testid="email-body"
            />
          </div>
          <div className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 px-3 py-2">
            Attachment: <span className="font-mono">{invoice.invoice_no}.pdf</span> (generated locally and uploaded).
          </div>
          {canSaveDefaults && invoice.carrier_code && (
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-blue-50 border border-blue-200 px-3 py-2 rounded-sm" data-testid="invoice-save-defaults-row">
              <input
                type="checkbox"
                checked={saveDefaults}
                onChange={(e) => setSaveDefaults(e.target.checked)}
                className="mt-0.5"
                data-testid="invoice-save-defaults"
              />
              <span>
                Save these addresses on <span className="font-mono font-bold">{invoice.carrier_code}</span> so the next invoice for this partner pre-fills automatically.
              </span>
            </label>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="ps-btn ps-btn-primary" onClick={send} disabled={sending} data-testid="email-send">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Tab 1: Saved Invoices history ---
function SavedInvoicesTab({ canDelete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState({ carrier_code: "", invoice_type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.carrier_code) params.append("carrier_code", filter.carrier_code);
      if (filter.invoice_type) params.append("invoice_type", filter.invoice_type);
      const { data } = await api.get(`/invoices?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openInvoice = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Row-level "Download PDF" — fetches the full invoice (with lines) so the
  // renderer has everything it needs, then dispatches to the right builder
  // (standard wash/handling, FDA or Water Solution) based on invoice_type.
  const downloadInvoiceById = async (id, invoice_no) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      await downloadInvoicePdf(data);
    } catch (e) {
      toast.error(`Could not generate PDF for ${invoice_no}: ${formatApiError(e)}`);
    }
  };

  const exportList = () => {
    if (!items.length) { toast.error("Nothing to export"); return; }
    downloadCsv(items, "invoices_list", [
      { key: "invoice_no", label: "Invoice No" },
      { key: "carrier_code", label: "Carrier" },
      { key: "currency", label: "Currency" },
      { key: "date_from", label: "From", format: (v) => v?.slice(0, 10) },
      { key: "date_to", label: "To", format: (v) => v?.slice(0, 10) },
      { key: "totals", label: "Containers", format: (v) => v?.containers ?? 0 },
      { key: "totals", label: "Line Items", format: (v) => v?.line_items ?? 0 },
      { key: "totals", label: "Grand Total", format: (v) => Number(v?.grand_total ?? 0).toFixed(2) },
      { key: "status", label: "Status" },
      { key: "created_at", label: "Issued At" },
      { key: "created_by", label: "Issued By" },
    ]);
  };

  if (selected) {
    return (
      <InvoiceDetail
        invoice={selected}
        onBack={() => setSelected(null)}
        canDelete={canDelete}
        onDeleted={() => { setSelected(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <input
            placeholder="Filter by carrier code…"
            value={filter.carrier_code}
            onChange={(e) => setFilter((f) => ({ ...f, carrier_code: e.target.value.toUpperCase() }))}
            className="ps-input font-mono max-w-xs"
            data-testid="filter-invoice-carrier"
          />
          {/* Type filter — quick way to isolate rental FDAs from the depot
              handling/storage/washing invoices. Empty = all types. */}
          <select
            value={filter.invoice_type}
            onChange={(e) => setFilter((f) => ({ ...f, invoice_type: e.target.value }))}
            className="ps-input max-w-[10rem]"
            data-testid="filter-invoice-type"
          >
            <option value="">All types</option>
            <option value="RENTAL">Rental (FDA)</option>
            <option value="HANDLING">Handling</option>
            <option value="STORAGE">Storage</option>
            <option value="WASHING">Washing</option>
            <option value="WATER_SOLUTION">Water Solution</option>
            <option value="REPAIR">Repair</option>
            <option value="MANUAL_PO">Manual PO</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-invoices">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button className="ps-btn ps-btn-secondary" onClick={exportList} data-testid="export-invoices-csv">
            <Download className="w-4 h-4" /> Export list (CSV)
          </button>
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="invoices-list-table">
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Carrier</th>
              <th>Period</th>
              <th className="text-right">Containers</th>
              <th className="text-right">Lines</th>
              <th className="text-right">Total</th>
              <th>Status</th>
              <th>Issued</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400">
                No saved invoices yet. Switch to <strong>Generate New</strong> to create your first invoice.
              </td></tr>
            )}
            {!loading && items.map((inv) => (
              <tr key={inv.id || inv.invoice_no} data-testid={`saved-invoice-${inv.invoice_no}`}>
                <td className="font-mono font-bold text-gray-900">{inv.invoice_no}</td>
                <td className="font-mono">{inv.carrier_code}</td>
                <td className="text-xs text-gray-600 font-mono">
                  {inv.date_from?.slice(0,10)} → {inv.date_to?.slice(0,10)}
                </td>
                <td className="text-right font-mono">{inv.totals?.containers ?? 0}</td>
                <td className="text-right font-mono">{inv.totals?.line_items ?? 0}</td>
                <td className="text-right font-mono font-bold text-[#006E9E]">
                  {fmtMoney(inv.totals?.grand_total, inv.currency)}
                </td>
                <td>
                  <span className="ps-pill ps-pill-success">
                    <span className="dot" /> {inv.status || "ISSUED"}
                  </span>
                </td>
                <td className="text-xs text-gray-500 font-mono">
                  {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="text-right">
                  <div className="inline-flex gap-1">
                    <button
                      className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                      onClick={() => openInvoice(inv.id || inv.invoice_no)}
                      data-testid={`open-invoice-${inv.invoice_no}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <button
                      className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                      onClick={() => downloadInvoiceById(inv.id || inv.invoice_no, inv.invoice_no)}
                      title="Download PDF"
                      data-testid={`download-invoice-${inv.invoice_no}`}
                    >
                      <Printer className="w-3.5 h-3.5" /> PDF
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Tab 2: Generate New ---
function GenerateInvoiceTab({ onSaved, canSave = true }) {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 86400000);
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState("");
  const [dateFrom, setDateFrom] = useState(fmtDateInput(monthAgo));
  const [dateTo, setDateTo] = useState(fmtDateInput(today));
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [washInvOpen, setWashInvOpen] = useState(false);

  useEffect(() => {
    api.get("/tariffs").then((r) => {
      setCarriers(r.data);
      if (r.data.length && !carrier) setCarrier(r.data[0].carrier_code);
    });
    // eslint-disable-next-line
  }, []);

  const generate = async (serviceFilter = null) => {
    if (!carrier) { toast.error("Select a carrier first"); return; }
    setLoading(true);
    setInvoice(null);
    setSaved(false);
    try {
      const params = new URLSearchParams({
        carrier_code: carrier,
        date_from: new Date(dateFrom).toISOString(),
        date_to: new Date(dateTo + "T23:59:59").toISOString(),
      });
      if (serviceFilter) params.append("service_filter", serviceFilter);
      const { data } = await api.get(`/invoices/generate?${params.toString()}`);
      setInvoice(data);
      const suffix = serviceFilter ? ` [${serviceFilter} only]` : "";
      toast.success(`Invoice ${data.invoice_no}${suffix} ready — ${data.totals.containers} containers, ${fmtMoney(data.totals.grand_total, data.currency)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!invoice) return;
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", invoice);
      setInvoice(data);
      setSaved(true);
      toast.success(`Invoice ${data.invoice_no} saved`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Carrier handling / storage / PTI invoices are generated below. Tank-wash billing has its own monthly generator →
        </div>
        <button className="ps-btn ps-btn-secondary shrink-0" onClick={() => setWashInvOpen(true)} data-testid="generate-wash-invoice-open">
          <Droplets className="w-4 h-4" /> Monthly wash invoice
        </button>
      </div>
      {washInvOpen && (
        <WashInvoiceModal
          onClose={() => setWashInvOpen(false)}
          onCreated={() => { setWashInvOpen(false); onSaved?.(); }}
        />
      )}
      <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-4 gap-3" data-testid="invoice-generate-form">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier</label>
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="ps-input font-mono" data-testid="invoice-carrier">
            <option value="">— select —</option>
            {carriers.map((c) => <option key={c.carrier_code} value={c.carrier_code}>{c.carrier_code} ({c.currency})</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ps-input font-mono" data-testid="invoice-from" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="ps-input font-mono" data-testid="invoice-to" />
        </div>
        <div className="flex items-end gap-2">
          <button className="ps-btn ps-btn-primary flex-1"
                  onClick={save}
                  disabled={!invoice || saving || saved || !canSave}
                  data-testid="save-invoice"
                  title={!canSave ? "Preview-only — Finance saves the final invoice" : undefined}>
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : saved ? <>Saved ✓</>
              : !canSave ? <>Preview only</>
              : <><Save className="w-4 h-4" /> Save Invoice</>}
          </button>
          <button className="ps-btn ps-btn-secondary" onClick={() => invoice && invoiceToCsv(invoice)} disabled={!invoice} data-testid="export-invoice-csv" title="CSV">
            <FileSpreadsheet className="w-4 h-4" />
          </button>
          <button className="ps-btn ps-btn-secondary" onClick={() => invoice && downloadInvoicePdf(invoice)} disabled={!invoice} data-testid="export-invoice-pdf" title="PDF">
            <Printer className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Split-service preview row — each button pulls a single service group
          as its own invoice so Finance can raise Handling / Storage / Washing
          separately per carrier request. The "All-in-one" button keeps the
          legacy behaviour for anyone who still wants a combined bill. */}
      <div className="border border-gray-200 bg-white p-4" data-testid="invoice-service-picker">
        <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-2">Generate preview by service</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: "Handling only", svc: "HANDLING", icon: <ArrowRight className="w-4 h-4" /> , testid: "generate-handling" },
            { label: "Storage only",  svc: "STORAGE",  icon: <Inbox className="w-4 h-4" />,       testid: "generate-storage" },
            { label: "Washing only",  svc: "WASHING",  icon: <Droplets className="w-4 h-4" />,     testid: "generate-washing" },
            { label: "All-in-one",    svc: null,       icon: <Receipt className="w-4 h-4" />,      testid: "generate-invoice" },
          ].map((b) => {
            const active = invoice && (invoice.service_filter || null) === b.svc && invoice.carrier_code === carrier;
            return (
              <button
                key={b.label}
                onClick={() => generate(b.svc)}
                disabled={loading}
                className={`ps-btn ${active ? "ps-btn-primary" : "ps-btn-secondary"} justify-center`}
                data-testid={b.testid}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : b.icon}
                {b.label}
              </button>
            );
          })}
        </div>
      </div>

      {invoice && (
        <div data-testid="invoice-preview">
          <div className="border border-gray-200 bg-white">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
                  {saved ? "Saved invoice" : "Preview (not saved yet)"}
                  {invoice.service_filter && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#006E9E] text-white rounded-sm text-[9px]" data-testid="invoice-service-badge">
                      {invoice.service_filter} ONLY
                    </span>
                  )}
                </div>
                <div className="font-mono font-bold text-lg">{invoice.invoice_no}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand Total</div>
                <div className="font-mono font-black text-3xl text-[#006E9E]">{fmtMoney(invoice.totals.grand_total, invoice.currency)}</div>
              </div>
            </div>
            <div className="grid grid-cols-4 divide-x divide-gray-200">
              {[
                { l: "Carrier", v: invoice.carrier_code },
                { l: "Containers", v: invoice.totals.containers.toLocaleString() },
                { l: "Line items", v: invoice.totals.line_items.toLocaleString() },
                { l: "Free days", v: invoice.free_days },
              ].map((m) => (
                <div key={m.l} className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{m.l}</div>
                  <div className="font-mono text-xl font-bold mt-1">{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          {invoice.wash_orders_merged > 0 && (
            <div
              className="border border-blue-200 bg-blue-50 text-blue-900 px-4 py-2.5 mt-4 flex items-start gap-2 text-sm"
              data-testid="invoice-wash-merged-note"
            >
              <Droplets className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-bold">{invoice.wash_orders_merged}</span> wash-order{invoice.wash_orders_merged === 1 ? "" : "s"} merged in from <code className="font-mono text-xs bg-white px-1 py-0.5 rounded">wash_orders</code> (stand-alone washes without a matching gate-event WASHING service — e.g. carrier monthly backfills). Dedup key: <span className="font-mono text-xs">(tank, YYYY-MM-DD)</span>.
              </div>
            </div>
          )}

          {invoice.category_breakdown && invoice.category_breakdown.length > 1 && (
            <div className="border border-gray-200 bg-white p-4 mt-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Category breakdown</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="invoice-preview-category-breakdown">
                {invoice.category_breakdown.map((c) => {
                  const accent = c.category === "FOOD" ? "#99D420"
                    : c.category === "CHEM" ? "#F1471D" : "#5A4E42";
                  return (
                    <div key={c.category} className="border-l-4 pl-3 py-1.5" style={{ borderColor: accent }}>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">{c.category}</div>
                      <div className="font-mono text-lg font-bold mt-0.5">{fmtMoney(c.subtotal, invoice.currency)}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{c.containers} container{c.containers === 1 ? "" : "s"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border border-gray-200 bg-white overflow-x-auto mt-4">
            <table className="ps-table" data-testid="invoice-preview-table">
              <thead>
                <tr>
                  <th>Container</th>
                  <th>ISO</th>
                  <th>Size</th>
                  <th>Category</th>
                  <th>Customer</th>
                  <th className="text-right">Line items</th>
                  <th className="text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {invoice.containers.slice(0, 200).map((c, idx) => {
                  const cat = c.tank_category || null;
                  const prevCat = idx > 0 ? (invoice.containers[idx - 1].tank_category || null) : null;
                  const showSep = idx === 0 || cat !== prevCat;
                  const catLabel = cat || "UNCATEGORIZED";
                  const catColor = cat === "FOOD" ? "#99D420" : cat === "CHEM" ? "#F1471D" : "#9CA3AF";
                  return (
                    <Fragment key={c.container_number}>
                      {showSep && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: catColor }}>
                            ── {catLabel === "FOOD" ? "Food-grade cleanings" : catLabel === "CHEM" ? "Chemical cleanings" : "Uncategorized"}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="font-mono font-semibold">{c.container_number}</td>
                        <td className="font-mono">{c.iso_type}</td>
                        <td className="font-mono">{c.size_ft}ft</td>
                        <td>
                          {cat ? (
                            <span className="text-[10px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${catColor}22`, color: catColor }}>
                              {cat}
                            </span>
                          ) : <span className="text-gray-400 text-xs">—</span>}
                        </td>
                        <td className="text-xs">{c.customer || "—"}</td>
                        <td className="text-right font-mono">{c.lines.length}</td>
                        <td className="text-right font-mono font-bold">{fmtMoney(c.subtotal, invoice.currency)}</td>
                      </tr>
                    </Fragment>
                  );
                })}
                {invoice.containers.length > 200 && (
                  <tr><td colSpan={7} className="text-center text-xs text-gray-400 py-3">
                    Showing first 200 of {invoice.containers.length}. Save & view detail for full list with line items.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Tab 3: Received Invoices (HPL Purchase Orders) ---
function ReceivedInvoicesTab({ canEdit, canDelete }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/received-invoices");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let okCount = 0, errCount = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/received-invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        okCount++;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
        errCount++;
      }
    }
    if (okCount) toast.success(`${okCount} PO file(s) ingested`);
    if (e.target) e.target.value = "";
    setUploading(false);
    load();
  };

  const remove = async (rid, po) => {
    if (!window.confirm(`Delete received PO ${po}?`)) return;
    try {
      await api.delete(`/received-invoices/${rid}`);
      toast.success(`Deleted ${po}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportList = () => {
    if (!items.length) { toast.error("Nothing to export"); return; }
    downloadCsv(items, "received_invoices", [
      { key: "po_number", label: "PO Number" },
      { key: "carrier_code", label: "Carrier" },
      { key: "invoice_type", label: "Type" },
      { key: "currency", label: "Currency" },
      { key: "grand_total", label: "Total", format: (v) => Number(v ?? 0).toFixed(2) },
      { key: "issue_date", label: "Issue Date" },
      { key: "period_start", label: "Period Start" },
      { key: "period_end", label: "Period End" },
      { key: "remark", label: "Remark" },
      { key: "source_file", label: "Source File" },
      { key: "uploaded_at", label: "Uploaded At" },
    ]);
  };

  if (selected) {
    return (
      <div className="space-y-4" data-testid="received-invoice-detail">
        <div className="flex items-center justify-between">
          <button className="ps-btn ps-btn-secondary" onClick={() => setSelected(null)} data-testid="received-back">
            <ArrowLeft className="w-4 h-4" /> Back to list
          </button>
          {canDelete && (
            <button className="ps-btn ps-btn-secondary text-red-600"
                    onClick={() => remove(selected.id, selected.po_number)}
                    data-testid="received-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>

        <div className="border border-gray-200 bg-white">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">
                Received Purchase Order
              </div>
              <div className="font-mono font-bold text-lg">{selected.po_number}</div>
              <div className="text-xs text-gray-500 mt-1">{selected.remark}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Grand Total</div>
              <div className="font-mono font-black text-3xl text-[#006E9E]">
                {fmtMoney(selected.grand_total, selected.currency)}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 divide-x divide-gray-200">
            {[
              { l: "Type", v: selected.invoice_type },
              { l: "Carrier", v: selected.carrier_code },
              { l: "Issue Date", v: selected.issue_date || "—" },
              { l: "Period", v: selected.period_start ? `${selected.period_start} → ${selected.period_end}` : "—" },
            ].map((m) => (
              <div key={m.l} className="p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{m.l}</div>
                <div className="font-mono text-sm font-bold mt-1 truncate">{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="received-lines-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Activity</th>
                <th className="text-right">Base Price</th>
                <th>Per</th>
                <th>Unit</th>
                <th className="text-right">Quantity</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(selected.lines || []).map((ln) => (
                <tr key={ln.no}>
                  <td className="font-mono">{ln.no}</td>
                  <td>{ln.activity}</td>
                  <td className="text-right font-mono">{Number(ln.base_price ?? 0).toFixed(2)}</td>
                  <td className="font-mono text-xs">{ln.per}</td>
                  <td className="font-mono text-xs">{ln.unit}</td>
                  <td className="text-right font-mono">{Number(ln.quantity ?? 0).toLocaleString()}</td>
                  <td className="text-right font-mono font-bold">
                    {fmtMoney(ln.total, selected.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border border-blue-100 bg-blue-50/50 p-4 text-xs text-gray-700">
        <strong className="block uppercase tracking-[0.16em] text-[#006E9E] mb-1">
          Carrier purchase orders received from HPL (Hapag-Lloyd)
        </strong>
        Upload PTWO <code className="font-mono">.xls</code> files here for reconciliation against
        the LoLo invoices MTG generates for HLC. Two types are detected automatically:{" "}
        <strong>LOLO</strong> (USD, 20'/40' lift on/off) and <strong>VAN_TOLL</strong> (GHS, transport fees).
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500 font-mono">
          {items.length} received PO{items.length === 1 ? "" : "s"} on file
        </div>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-received">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button className="ps-btn ps-btn-secondary" onClick={exportList} data-testid="export-received-csv">
            <Download className="w-4 h-4" /> Export (CSV)
          </button>
          {canEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".xls,.xlsx"
                multiple
                onChange={handleUpload}
                className="hidden"
                data-testid="received-file-input"
              />
              <button
                className="ps-btn ps-btn-primary"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-received-po"
              >
                {uploading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
                  : <><Upload className="w-4 h-4" /> Upload PO files</>}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="ps-table" data-testid="received-list-table">
          <thead>
            <tr>
              <th>PO Number</th>
              <th>Type</th>
              <th>Carrier</th>
              <th>Period / Remark</th>
              <th>Issue Date</th>
              <th className="text-right">Total</th>
              <th>Source File</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center py-10 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400">
                No received POs yet. Click <strong>Upload PO files</strong> and select PTWO <code>.xls</code> files from HPL.
              </td></tr>
            )}
            {!loading && items.map((r) => (
              <tr key={r.id || r.po_number} data-testid={`received-row-${r.po_number}`}>
                <td className="font-mono font-bold text-gray-900">{r.po_number}</td>
                <td>
                  <span className={`ps-pill ${r.invoice_type === "LOLO" ? "ps-pill-success" : "ps-pill-warn"}`}>
                    <span className="dot" /> {r.invoice_type}
                  </span>
                </td>
                <td className="font-mono">{r.carrier_code}</td>
                <td className="text-xs text-gray-600">
                  {r.period_start
                    ? <span className="font-mono">{r.period_start} → {r.period_end}</span>
                    : <span className="italic">{r.remark || "—"}</span>}
                </td>
                <td className="text-xs font-mono text-gray-600">{r.issue_date || "—"}</td>
                <td className="text-right font-mono font-bold text-[#006E9E]">
                  {fmtMoney(r.grand_total, r.currency)}
                </td>
                <td className="text-xs text-gray-500 font-mono truncate max-w-[200px]">{r.source_file}</td>
                <td className="text-right">
                  <button
                    className="ps-btn ps-btn-secondary h-7 px-3 text-xs"
                    onClick={() => setSelected(r)}
                    data-testid={`open-received-${r.po_number}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- Tab 4: Reconciliation (Received POs vs Generated Invoices) ---
function ReconciliationTab() {
  const today = new Date();
  const ninetyDaysAgo = new Date(today.getTime() - 90 * 86400000);
  const [carriers, setCarriers] = useState([]);
  const [carrier, setCarrier] = useState("HLC");
  const [dateFrom, setDateFrom] = useState(fmtDateInput(ninetyDaysAgo));
  const [dateTo, setDateTo] = useState(fmtDateInput(today));
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/tariffs").then((r) => {
      // Always include HLC even if no tariff (HPL is the only carrier currently with received POs)
      const list = r.data || [];
      const codes = new Set(list.map((c) => c.carrier_code));
      if (!codes.has("HLC")) list.unshift({ carrier_code: "HLC", currency: "USD" });
      setCarriers(list);
    });
  }, []);

  const load = useCallback(async () => {
    if (!carrier) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get(`/received-invoices/_reconcile/${carrier}?${params.toString()}`),
        api.get(`/dashboard/reconciliation-snapshot?carrier_code=${carrier}&months=12`),
      ]);
      setData(d);
      // Trim chart rows to non-empty window (skip leading months with both zero)
      const rows = (c.rows || []);
      const firstNonZero = rows.findIndex((r) => r.received !== 0 || r.generated !== 0);
      const lastNonZero = (() => {
        for (let i = rows.length - 1; i >= 0; i--)
          if (rows[i].received !== 0 || rows[i].generated !== 0) return i;
        return -1;
      })();
      setChart({
        ...c,
        rows: firstNonZero >= 0 ? rows.slice(firstNonZero, lastNonZero + 1) : rows,
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [carrier, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [];
    (data.received || []).forEach((r) => rows.push({
      side: "RECEIVED",
      ref: r.po_number,
      type: r.invoice_type,
      currency: r.currency,
      amount: r.grand_total,
      issue_date: r.issue_date,
      period: r.period_start ? `${r.period_start} → ${r.period_end}` : (r.remark || ""),
    }));
    (data.generated || []).forEach((g) => rows.push({
      side: "GENERATED",
      ref: g.invoice_no,
      type: "INVOICE",
      currency: g.currency,
      amount: (g.totals || {}).grand_total ?? 0,
      issue_date: g.created_at?.slice(0, 10),
      period: `${(g.date_from || "").slice(0, 10)} → ${(g.date_to || "").slice(0, 10)}`,
    }));
    if (!rows.length) { toast.error("Nothing to export"); return; }
    downloadCsv(rows, `reconcile_${carrier}_${dateFrom}_${dateTo}`, [
      { key: "side", label: "Side" },
      { key: "ref", label: "Reference" },
      { key: "type", label: "Type" },
      { key: "currency", label: "Currency" },
      { key: "amount", label: "Amount", format: (v) => Number(v ?? 0).toFixed(2) },
      { key: "issue_date", label: "Issue Date" },
      { key: "period", label: "Period / Remark" },
    ]);
  };

  const totals = data?.totals_by_currency || {};
  const serviceTotals = data?.service_totals_by_currency || {};
  const totalCurrencies = Object.keys(totals);

  // Re-frame the apples-to-apples comparison: HPL's PTWO only covers HANDLING (LIFT ON/OFF).
  // Storage is billed separately by us and HPL doesn't acknowledge it via PTWO.
  // So the headline delta on each currency = (our HANDLING line) − (HPL PO total).
  const compareCarriers = {
    HLC: { received_label: "HPL handling acknowledged", our_service: "HANDLING", note: "HPL PTWOs cover lift-on/lift-off only — storage is invoiced separately." },
  };
  const compareCfg = compareCarriers[carrier] || { received_label: `${carrier} acknowledged`, our_service: null, note: "" };

  return (
    <div className="space-y-4">
      <div className="border border-blue-100 bg-blue-50/50 p-4 text-xs text-gray-700">
        <strong className="block uppercase tracking-[0.16em] text-[#006E9E] mb-1">
          Carrier reconciliation
        </strong>
        Compare what the carrier acknowledged paying us (left) against what our system invoiced for the same window (right).
        The headline delta uses an apples-to-apples comparison: <strong>handling-only</strong> on our side vs the carrier&apos;s PO total
        (carriers like HPL only acknowledge lift fees via PTWO; storage is invoiced separately).
        The service breakdown panel underneath shows where every dollar goes.
      </div>

      <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-5 gap-3" data-testid="reconcile-filters">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier</label>
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="ps-input font-mono" data-testid="reconcile-carrier">
            {carriers.map((c) => (
              <option key={c.carrier_code} value={c.carrier_code}>
                {c.carrier_code} ({c.currency})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ps-input font-mono" data-testid="reconcile-from" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="ps-input font-mono" data-testid="reconcile-to" />
        </div>
        <div className="flex items-end">
          <button className="ps-btn ps-btn-primary w-full" onClick={load} disabled={loading} data-testid="reconcile-run">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : <><RefreshCw className="w-4 h-4" /> Recalculate</>}
          </button>
        </div>
        <div className="flex items-end">
          <button className="ps-btn ps-btn-secondary w-full" onClick={exportCsv} disabled={!data} data-testid="reconcile-export">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {data && totalCurrencies.length === 0 && (
        <div className="border border-gray-200 bg-white p-10 text-center text-gray-400 text-sm">
          No received POs <em>or</em> generated invoices found for <strong>{carrier}</strong> in this date window.
        </div>
      )}

      {totalCurrencies.map((ccy) => {
        const t = totals[ccy];
        const services = serviceTotals[ccy] || {};
        const handlingAmt = (services.HANDLING || {}).amount || 0;
        const storageAmt = (services.STORAGE || {}).amount || 0;
        const otherServices = Object.entries(services).filter(([k]) => k !== "HANDLING" && k !== "STORAGE");
        // Apples-to-apples delta: HPL acknowledges only handling; our HANDLING vs their total.
        const compareOur = compareCfg.our_service === "HANDLING" ? handlingAmt : t.generated;
        const compareOurLabel = compareCfg.our_service === "HANDLING" ? "Our handling invoiced" : "We invoiced";
        const delta = +(compareOur - t.received).toFixed(2);
        const absDelta = Math.abs(delta);
        const isMatch = absDelta < 0.01;
        const isUnderBilled = delta < 0;
        const deltaColor = isMatch ? "text-[#99D420]" : isUnderBilled ? "text-[#F1471D]" : "text-[#006E9E]";
        const deltaLabel = isMatch ? "Match ✓" : isUnderBilled ? "Under-billed" : "Over-billed";
        const explainer = isMatch
          ? "Handling reconciliation balanced. No action required."
          : isUnderBilled
          ? `${carrier} acknowledges ${fmtMoney(absDelta, ccy)} more in handling than we invoiced — investigate missing lifts in our gate events or raise a top-up invoice.`
          : `Our handling invoice is ${fmtMoney(absDelta, ccy)} above ${carrier}'s PO — wait for ${carrier}'s next PO or issue a credit note for the difference.`;
        return (
          <div key={ccy} className="border border-gray-200 bg-white" data-testid={`reconcile-summary-${ccy}`}>
            <div className="grid grid-cols-3 divide-x divide-gray-200">
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{compareCfg.received_label} ({ccy})</div>
                <div className="font-mono font-black text-3xl mt-1 text-gray-900" data-testid={`reconcile-received-total-${ccy}`}>{fmtMoney(t.received, ccy)}</div>
                <div className="text-[10px] text-gray-500 mt-1">Sum of {(data.received || []).filter((r) => r.currency === ccy).length} PO(s) {carrier} sent us</div>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{compareOurLabel} ({ccy})</div>
                <div className="font-mono font-black text-3xl mt-1 text-gray-900" data-testid={`reconcile-generated-total-${ccy}`}>{fmtMoney(compareOur, ccy)}</div>
                <div className="text-[10px] text-gray-500 mt-1">{compareCfg.our_service === "HANDLING" ? `Handling line only — ${(services.HANDLING || {}).lines || 0} lift(s) across ${(data.generated || []).filter((g) => (g.currency || "USD") === ccy).length} invoice(s)` : `Sum of ${(data.generated || []).filter((g) => (g.currency || "USD") === ccy).length} MTG invoice(s)`}</div>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{deltaLabel}</div>
                <div className={`font-mono font-black text-3xl mt-1 ${deltaColor}`} data-testid={`reconcile-delta-${ccy}`}>
                  {delta > 0 ? "+" : ""}{fmtMoney(delta, ccy)}
                </div>
                <div className={`text-[10px] uppercase tracking-[0.16em] font-bold mt-1 ${deltaColor}`}>(invoiced − acknowledged)</div>
              </div>
            </div>
            <div className={`px-5 py-3 border-t border-gray-200 text-[12px] leading-relaxed ${isMatch ? "bg-[#F4FBE6] text-gray-700" : isUnderBilled ? "bg-[#FFF1ED] text-gray-800" : "bg-[#EBF5FB] text-gray-800"}`} data-testid={`reconcile-explainer-${ccy}`}>
              <strong className={isMatch ? "text-[#5BA508]" : isUnderBilled ? "text-[#F1471D]" : "text-[#006E9E]"}>{deltaLabel}.</strong> {explainer}
              {compareCfg.note && <span className="block text-[11px] text-gray-500 mt-1">{compareCfg.note}</span>}
            </div>

            {/* Service breakdown — what makes up our total invoice */}
            <div className="border-t border-gray-200 bg-[#FBF8EC]/40 px-5 py-4" data-testid={`reconcile-service-breakdown-${ccy}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-600 mb-3">
                Service breakdown — our invoiced total ({fmtMoney(t.generated, ccy)})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#006E9E]">Handling</div>
                  <div className="font-mono text-xl font-bold text-gray-900 mt-1" data-testid={`reconcile-handling-${ccy}`}>{fmtMoney(handlingAmt, ccy)}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{(services.HANDLING || {}).lines || 0} lift(s)</div>
                </div>
                <div className="bg-white border border-gray-200 px-4 py-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#99D420]">Storage</div>
                  <div className="font-mono text-xl font-bold text-gray-900 mt-1" data-testid={`reconcile-storage-${ccy}`}>{fmtMoney(storageAmt, ccy)}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{(services.STORAGE || {}).lines || 0} container-day line(s)</div>
                </div>
                {otherServices.map(([svc, info]) => (
                  <div key={svc} className="bg-white border border-gray-200 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#F1471D]">{svc}</div>
                    <div className="font-mono text-xl font-bold text-gray-900 mt-1">{fmtMoney(info.amount, ccy)}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{info.lines} line(s)</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      {chart && chart.rows?.length > 0 && (
        <div className="border border-gray-200 bg-white" data-testid="reconcile-chart">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#006E9E]" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700">
                Monthly trend — {carrier}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                Per-month received PO total vs generated invoice total (USD only)
              </div>
            </div>
          </div>
          <div className="p-5">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chart.rows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D2" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: "monospace" }}
                  stroke="#94A3B8"
                  tickFormatter={(v) => {
                    const n = Number(v || 0);
                    if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
                    return `$${n}`;
                  }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-gray-200 shadow-lg p-3 text-xs font-mono">
                        <div className="font-bold text-gray-900 mb-1.5">{label}</div>
                        {payload.map((p) => (
                          <div key={p.dataKey} className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                            <span className="text-gray-600 mr-2">{p.name}:</span>
                            <span className="font-bold text-gray-900 ml-auto">
                              USD {Number(p.value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                <Bar dataKey="received" fill="#008DC6" radius={[4, 4, 0, 0]} name="Received (carrier)" />
                <Bar dataKey="generated" fill="#99D420" radius={[4, 4, 0, 0]} name="Generated (MTG)" />
                <Line
                  type="monotone"
                  dataKey="delta"
                  stroke="#F1471D"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "#F1471D" }}
                  name="Delta (gen − rec)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {data && (data.received?.length || data.generated?.length) > 0 && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="border border-gray-200 bg-white" data-testid="reconcile-received-panel">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <Inbox className="w-4 h-4 text-[#006E9E]" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-700">
                Received ({data.received?.length || 0})
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Type</th>
                    <th>Period</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.received || []).length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-gray-400 text-xs">No POs in this window.</td></tr>
                  )}
                  {(data.received || []).map((r) => (
                    <tr key={r.po_number} data-testid={`reconcile-received-${r.po_number}`}>
                      <td className="font-mono font-bold">{r.po_number}</td>
                      <td>
                        <span className={`ps-pill ${r.invoice_type === "LOLO" ? "ps-pill-success" : "ps-pill-warn"}`}>
                          <span className="dot" /> {r.invoice_type}
                        </span>
                      </td>
                      <td className="text-xs text-gray-600">
                        {r.period_start
                          ? <span className="font-mono">{r.period_start} → {r.period_end}</span>
                          : <span className="italic">{r.remark || r.issue_date || "—"}</span>}
                      </td>
                      <td className="text-right font-mono font-bold">{fmtMoney(r.grand_total, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border border-gray-200 bg-white" data-testid="reconcile-generated-panel">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-[#006E9E]" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-700">
                Generated ({data.generated?.length || 0})
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th className="text-right">Containers</th>
                    <th>Period</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.generated || []).length === 0 && (
                    <tr><td colSpan={4} className="text-center py-6 text-gray-400 text-xs">No invoices in this window.</td></tr>
                  )}
                  {(data.generated || []).map((g) => (
                    <tr key={g.id || g.invoice_no} data-testid={`reconcile-generated-${g.invoice_no}`}>
                      <td className="font-mono font-bold">{g.invoice_no}</td>
                      <td className="text-right font-mono">{(g.totals || {}).containers ?? 0}</td>
                      <td className="text-xs text-gray-600 font-mono">
                        {(g.date_from || "").slice(0, 10)} <ArrowRight className="w-3 h-3 inline" /> {(g.date_to || "").slice(0, 10)}
                      </td>
                      <td className="text-right font-mono font-bold">{fmtMoney((g.totals || {}).grand_total, g.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Invoices() {
  const { user } = useAuth();
  // Two separate capabilities:
  //   - canPreview: mirrors backend ROLES_FINANCE_VIEW — depot_manager, GM, CEO
  //     et al. can build a preview + export CSV/PDF, but they cannot commit
  //     the invoice to the ledger. This unlocks the Generate tab for Sedem
  //     et al. so ops can dry-run a month-end tally without waiting on Finance.
  //   - canEdit: mirrors backend ROLES_FINANCE_MANAGE — admin / finance /
  //     supervisor. Only these roles can hit the Save button.
  const canPreview = ["admin", "ceo", "general_manager", "depot_manager", "finance", "supervisor"].includes(user?.role);
  const canEdit = ["admin", "finance", "supervisor"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const [tab, setTab] = useState("saved");
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        subtitle="Generate, save and export carrier invoices based on tariffs and gate events."
        testid="invoices-header"
      />
      <div className="px-6 sm:px-8 pt-4 border-b border-gray-200 bg-white flex gap-1">
        {[
          { v: "saved", l: "Saved Invoices", i: FileText },
          ...(canPreview ? [{ v: "generate", l: "Generate New", i: Receipt }] : []),
          { v: "received", l: "Received (HPL)", i: Inbox },
          { v: "reconcile", l: "Reconciliation", i: Scale },
        ].map((t) => {
          const Icon = t.i;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={`tab-${t.v}`}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                tab === t.v ? "border-[#006E9E] text-[#006E9E]" : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>
      <div className="p-6 sm:p-8">
        {tab === "saved" && <SavedInvoicesTab key={refreshKey} canDelete={canDelete} />}
        {tab === "generate" && canPreview && <GenerateInvoiceTab onSaved={() => setRefreshKey((k) => k + 1)} canSave={canEdit} />}
        {tab === "received" && <ReceivedInvoicesTab canEdit={canEdit} canDelete={canDelete} />}
        {tab === "reconcile" && <ReconciliationTab />}
      </div>
    </div>
  );
}

// ─────────────── Wash-invoice generation modal ───────────────
function WashInvoiceModal({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [customerCode, setCustomerCode] = useState("");
  const [yyyyMm, setYyyyMm] = useState(defaultMonth);
  const [generating, setGenerating] = useState(false);
  const [tariffEditor, setTariffEditor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [carriersRes, tariffRes] = await Promise.all([
        api.get("/partners?role=carrier&fields=code,legal_name").catch(() => ({ data: [] })),
        api.get("/wash-tariffs").catch(() => ({ data: { items: [] } })),
      ]);
      if (cancelled) return;
      const list = Array.isArray(carriersRes.data) ? carriersRes.data : (carriersRes.data?.items || []);
      setCustomers(list.sort((x, y) => x.code.localeCompare(y.code)));
      setTariffs(tariffRes.data?.items || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const currentTariff = tariffs.find((t) => t.customer_code === customerCode);

  const generate = async (opts = {}) => {
    if (!customerCode) { toast.error("Pick a customer"); return; }
    if (!/^\d{4}-\d{2}$/.test(yyyyMm)) { toast.error("Month must look like 2026-06"); return; }
    const regenerate = !!opts.regenerate;
    if (regenerate) {
      const ok = window.confirm(
        `Regenerate INV-WASH-${customerCode}-${yyyyMm.replace("-", "")}?\n\n` +
        "The current DRAFT invoice will be deleted and rebuilt from wash orders. " +
        "Any SENT/APPROVED/PAID invoice must be voided first.",
      );
      if (!ok) return;
    }
    setGenerating(true);
    try {
      const res = await api.post("/invoices/generate-wash-monthly", {
        customer_code: customerCode, yyyy_mm: yyyyMm, regenerate,
      });
      if (res.data.created) {
        toast.success(`${regenerate ? "Rebuilt" : "Created"} ${res.data.invoice_no} — ${res.data.wash_count} washes, ${res.data.currency} ${res.data.subtotal.toLocaleString()}`);
      } else {
        toast.info(`${res.data.invoice_no} already exists — opening it.`);
      }
      onCreated(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="wash-invoice-modal">
      <div className="bg-white border border-gray-300 max-w-3xl w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-700" />
            <h2 className="text-lg font-bold tracking-tight">Generate wash invoice</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="wash-invoice-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-gray-600">
            Builds an <span className="font-mono font-bold">INV-WASH-&lt;customer&gt;-&lt;YYYYMM&gt;</span> DRAFT invoice
            from every CERTIFIED wash for that customer in the selected month. Each line carries the carrier
            reference (wash ref no). Pricing follows the wash type on each order — customer-specific tariff first,
            then the depot rate card.
          </p>
          <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer</label>
              <select className="ps-input font-mono" value={customerCode} onChange={(e) => setCustomerCode(e.target.value)} data-testid="wash-inv-customer">
                <option value="">— pick a customer —</option>
                {customers.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.legal_name || c.code}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Month</label>
              <input className="ps-input font-mono" type="month" value={yyyyMm} onChange={(e) => setYyyyMm(e.target.value)} data-testid="wash-inv-month" />
            </div>
            <button className="ps-btn ps-btn-primary" onClick={() => generate()} disabled={generating || !customerCode} data-testid="wash-inv-generate">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Receipt className="w-4 h-4" /> Generate</>}
            </button>
          </div>
          <div className="flex items-center justify-end -mt-2">
            <button
              type="button"
              onClick={() => generate({ regenerate: true })}
              disabled={generating || !customerCode}
              className="text-xs text-amber-800 hover:text-amber-900 underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
              data-testid="wash-inv-regenerate"
              title="Delete the existing DRAFT and rebuild from wash orders — use this after fixing pricing or adding missed washes."
            >
              Force regenerate (rebuild DRAFT from wash orders)
            </button>
          </div>
          <div className="border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-gray-700">
                Pricing {customerCode && (tariffs.find((t) => t.customer_code === customerCode) ? "— customer-specific tariff" : "— depot rate card")}
              </div>
            </div>
            {!customerCode ? (
              <div className="text-xs text-gray-500 italic">Select a customer to view the pricing that will apply.</div>
            ) : currentTariff ? (
              <table className="w-full text-sm font-mono">
                <tbody>
                  {currentTariff.rates.map((r, i) => (
                    <tr key={i} className="border-b border-gray-200 last:border-0">
                      <td className="py-1 w-40">{r.wash_category}</td>
                      <td className="py-1 text-right">{currentTariff.currency} {r.unit_price.toFixed(2)} / wash</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-xs text-amber-700">
                No rate card found — the invoice will use each wash&apos;s stored <span className="font-mono">unit_price</span>.
              </div>
            )}
            <div className="mt-2 text-[11px] text-gray-500">
              Rates are managed in one place: the <span className="font-bold">Tariffs</span> page (wash rate card + customer overrides).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
